import {
    MILITARY_VARIANT,
    STC_VARIANT,
    collection,
    isMilitaryEnabled,
    parentTypeFor,
} from "../_variants/index.js";
import {
    makeAddDesignChange,
    makeAddTransferredDesignChange,
    makeEditDesignChange,
    makeImportDesignChangesJobCards,
    makeLinkDesignChange,
    makeLockForRelease,
    makeUnlinkDesignChange,
    makeUnlockForRelease,
} from "./actions.js";

/**
 * Build the attachable-table descriptor for the Design Change register that
 * matches the supplied variant. The returned object is shape-compatible with
 * the previous `attachable.js` / `militaryAttachable.js` default exports.
 *
 * @param {import("../_variants/index.js").Variant} variant
 */
export const makeDesignChangeAttachable = (variant) => {
    const isMilitary = variant.key === "military";
    const labelSingular = isMilitary ? "Military Design Change" : "Design Change";
    const labelPlural = isMilitary ? "Military Design Changes" : "Design Changes";

    return {
        // `key` must match the parentBackref so back-references like
        // `Commercials/{id}/MilitaryDesignChanges` resolve correctly.
        "label": labelPlural,
        "key": parentTypeFor(variant, "DesignChanges"),
        "location": `${collection(variant, "DesignChanges")}/`,
        "actions": [
            {
                "label": `Add ${labelSingular}`,
                "action": makeAddDesignChange(variant),
                show({ meta }) {
                    return meta?.ParentType === "Commercials"
                        ? [true, '']
                        : [false, `Can only add a new ${labelSingular} to a Commercial`];
                }
            },
            {
                "label": "Add Transferred Design Change",
                "action": makeAddTransferredDesignChange(variant),
                show({ meta }) {
                    return meta?.ParentType === "Commercials"
                        ? [true, '']
                        : [false, `Can only add a new ${labelSingular} to a Commercial`];
                }
            },
            {
                "label": `Link ${labelSingular}`,
                "action": makeLinkDesignChange(variant),
                show() {
                    return [true, ''];
                }
            }
        ],
        "headers": [
            { "headerName": "Number", "field": "Number" },
            { "headerName": "Title", "field": "Title" },
            { "headerName": "Responsible Engineer", "field": "ResponsibleEngineer" },
            { "headerName": "Mod Type", "field": "Type" }
        ],
        "rowActions": [
            {
                "label": `Open ${labelSingular}`,
                action({ router, rowItem }) {
                    router.push(`/Database/${parentTypeFor(variant, "DesignChanges")}/${rowItem.ID}`);
                }
            },
            {
                "label": `Edit ${labelSingular}`,
                "action": makeEditDesignChange(variant)
            },
            {
                "label": "Import Job Cards to Commercial",
                "action": makeImportDesignChangesJobCards(variant),
                show({ meta }) {
                    return [meta.ParentType === "Commercials", "Can only perform this opperation within a commercial."];
                }
            },
            {
                "label": `Unlink this ${labelSingular}`,
                "action": makeUnlinkDesignChange(variant),
                show({ meta, pageItem, rowItem }) {
                    if (pageItem.Parent === rowItem.Number) {
                        return [false, "Cannot unlink this if the design change is the main parent."];
                    }
                    return [!!meta.ParentType, "Can only perform this opperation."];
                }
            },
            {
                "label": "Lock Release",
                "action": makeLockForRelease(variant),
                "break": true,
                show({ meta, rowItem }) {
                    if (rowItem.LockedForRelease === true) return [false, "Design Change is already locked."];
                    if (meta.ParentType !== "Commercials") return [false, "Can only lock a design change from a commercial."];
                    return [true, ""];
                }
            },
            {
                "label": "Unlock Release",
                "action": makeUnlockForRelease(variant),
                show({ meta, rowItem }) {
                    if (rowItem.LockedForRelease === false) return [false, "Design Change is not locked."];
                    if (meta.ParentType !== "Commercials") return [false, "Can only unlock a design change from a commercial."];
                    return [true, ""];
                }
            }
        ]
    };
};

// Pre-built attachables for direct use by other entities' routes/attachables.
// `default` keeps the historical import shape (`import dc from "../DesignChanges/attachable.js"`)
// working — it exposes the STC-variant attachable.
export const designChangeAttachable = makeDesignChangeAttachable(STC_VARIANT);
export const militaryDesignChangeAttachable = isMilitaryEnabled()
    ? makeDesignChangeAttachable(MILITARY_VARIANT)
    : null;

export default designChangeAttachable;
